import { isList, isListItem, isParagraph } from 'datocms-structured-text-utils';
import { ButtonV2Props } from 'libs/growth-platform-brand-system-v2/src/components/ButtonV2';
import {
  textBody,
  textBodyLarge,
  textTitle1,
  textTitle2,
} from 'libs/growth-platform-brand-system-v2/src/styles/typography';
import { ContentProps } from 'libs/growth-platform-brand-system-v2/src/templates/Content';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import {
  getGoogleAnalyticsId,
  track,
} from 'libs/growth-platform-brand-system-v2/src/utils/tracking';
import { createSchema, morphism } from 'morphism';
import React from 'react';
import { StructuredText, renderRule } from 'react-datocms';
import { ImagePropsGatsby, schemaImage } from '../components/schemaImage';
import { schemaDatoButton } from '../schemaDatoButton';

export const schemaContent = createSchema<
  ContentProps & { ctas?: ButtonV2Props[] },
  any
>({
  title: {
    path: 'contentTitle',
    fn: (propertyValue, source) => {
      return (
        <StructuredText
          data={propertyValue || source?.title}
          customNodeRules={[
            renderRule(isParagraph, ({ children, key }) => (
              <span
                key={key}
                className={classNames(
                  textTitle1,
                  source.isDark && source.backgroundColor !== 'white'
                    ? 'text-white'
                    : 'text-neutral-301',
                )}
              >
                {children}
              </span>
            )),
          ]}
        />
      );
    },
  },
  onClickVideoThumbnail: {
    path: [],
    fn: () => {
      return () =>
        track({
          gtm: {
            event: 'Toggle Interaction',
            properties: {
              componentName: 'Content',
              toggleLabel: 'NA/button open video modal',
              gaClientID: getGoogleAnalyticsId(),
            },
          },
        });
    },
  },
  subtitle: {
    path: 'contentSubtitle',
    fn: (propertyValue, source) => {
      return (
        <StructuredText
          data={propertyValue || source?.subtitle}
          customNodeRules={[
            renderRule(isParagraph, ({ children, key, ancestors }) => {
              if (isListItem(ancestors[0])) {
                return (
                  <p key={key} className="inline">
                    {children}
                  </p>
                );
              }

              return (
                <p
                  key={key}
                  className={classNames(
                    textBodyLarge,
                    source.isDark && source.backgroundColor !== 'white'
                      ? 'text-white'
                      : 'text-neutral-301',
                  )}
                >
                  {children}
                </p>
              );
            }),
            renderRule(isList, ({ children, key }) => (
              <ul
                key={key}
                className={classNames(
                  textBody,
                  'list-disc list-outside ml-1em',
                  source.isDark && source.backgroundColor !== 'white'
                    ? 'text-white'
                    : 'text-neutral-301',
                )}
              >
                {children}
              </ul>
            )),
          ]}
        />
      );
    },
  },

  image: {
    path: 'image',
    fn: (propertyValue: any): ImagePropsGatsby | undefined => {
      if (propertyValue) return morphism(schemaImage, propertyValue);
      return undefined;
    },
  },
  logo: {
    path: 'logo',
    fn: (propertyValue: any): ImagePropsGatsby | undefined => {
      if (propertyValue) {
        const imgProps = morphism(schemaImage, propertyValue);
        return imgProps;
      }
      return undefined;
    },
  },
  backgroundColor: 'backgroundColor',
  hasImageOnRight: 'hasImageOnRight',
  isEncapsulated: 'isEncapsulated',
  isDark: 'isDark',
  isImageBlackLayered: 'isImageBlackLayered',
  ctas: {
    path: ['ctas', 'locale'],
    fn: ({ ctas, locale }: any) =>
      ctas?.map((btn: any) => morphism(schemaDatoButton, { btn, locale })),
  },
  disabledAnimation: 'disabledAnimation',
  buttonDirection: {
    path: 'buttonDirection',
    fn: (propertieValue, source) => {
      if (source?.displayCtasVertically) {
        return source?.displayCtasVertically ? 'vertical' : 'horizontal';
      }
      return propertieValue;
    },
  },
});

export const schemaContentPricingES = createSchema<
  ContentProps & { ctas?: ButtonV2Props[] },
  any
>({
  ...schemaContent,
  disabledAnimation: { path: 'contentTitle', fn: () => true },
  title: {
    path: 'contentTitle',
    fn: (propertyValue, source) => {
      return (
        <StructuredText
          data={propertyValue || source?.title}
          customNodeRules={[
            renderRule(isParagraph, ({ children, key }) => (
              <span
                key={key}
                className={classNames(
                  textTitle2,
                  source.isDark && source.backgroundColor !== 'white'
                    ? 'text-white'
                    : 'text-neutral-301',
                )}
              >
                {children}
              </span>
            )),
          ]}
        />
      );
    },
  },
  subtitle: {
    path: 'contentSubtitle',
    fn: (propertyValue, source) => {
      return (
        <StructuredText
          data={propertyValue || source?.subtitle}
          customNodeRules={[
            renderRule(isParagraph, ({ children, key, ancestors }) => {
              if (isListItem(ancestors[0])) {
                return (
                  <p key={key} className="mb-8">
                    {children}
                  </p>
                );
              }

              return (
                <p
                  key={key}
                  className={classNames(
                    textBodyLarge,
                    source.isDark && source.backgroundColor !== 'white'
                      ? 'text-white'
                      : 'text-neutral-301',
                  )}
                >
                  {children}
                </p>
              );
            }),
            renderRule(isList, ({ children, key }) => (
              <ul
                key={key}
                className={classNames(
                  textBody,
                  'list-disc list-outside ml-1em mb-8',
                  source.isDark && source.backgroundColor !== 'white'
                    ? 'text-white'
                    : 'text-neutral-301',
                )}
              >
                {children}
              </ul>
            )),
          ]}
        />
      );
    },
  },
});
